import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  Grid,
  Divider,
  Chip
} from '@mui/material';
import { Save, Edit } from '@mui/icons-material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { UserUpdate } from '../types';
import LoadingButton from '../components/LoadingButton';

const ProfilePage: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: user?.email || '',
    password: '',
    confirmPassword: ''
  });

  const handleSave = async () => {
    if (!user) return;

    // Validate passwords match if changing password
    if (formData.password && formData.password !== formData.confirmPassword) {
      setError(t('profile.passwordsDoNotMatch', { defaultValue: 'Passwords do not match' }));
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const updateData: UserUpdate = {
        email: formData.email !== user.email ? formData.email : undefined,
        password: formData.password || undefined
      };

      // Remove undefined fields
      Object.keys(updateData).forEach(key => 
        updateData[key as keyof UserUpdate] === undefined && delete updateData[key as keyof UserUpdate]
      );

      if (Object.keys(updateData).length > 0) {
        await apiService.updateUser(user.id, updateData);
        await refreshUser();
        toast.success('Profile updated successfully');
      }

      setIsEditing(false);
      setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));

    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(err.detail || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      email: user?.email || '',
      password: '',
      confirmPassword: ''
    });
    setError('');
  };

  if (!user) {
    return (
      <Box>
        <Alert severity="error">Unable to load user profile</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('profile.profile')}
        </Typography>
        {!isEditing ? (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setIsEditing(true)}
          >
            {t('profile.editProfile')}
          </Button>
        ) : (
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={handleCancel}
            >
              {t('common.cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              loading={isLoading}
            >
              {t('common.save')} {t('common.changes', { defaultValue: 'Changes' })}
            </LoadingButton>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Profile Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('profile.accountInformation')}
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={3}>
                <TextField
                  label={t('profile.emailAddress')}
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!isEditing}
                  variant={isEditing ? "outlined" : "standard"}
                  InputProps={{
                    readOnly: !isEditing,
                  }}
                />

                {isEditing && (
                  <>
                    <Divider />
                    <Typography variant="subtitle1" color="text.secondary">
                      Change Password (leave empty to keep current password)
                    </Typography>
                    
                    <TextField
                      label="New Password"
                      type="password"
                      fullWidth
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      helperText="Minimum 8 characters"
                    />
                    
                    <TextField
                      label="Confirm New Password"
                      type="password"
                      fullWidth
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
                      helperText={
                        formData.password !== formData.confirmPassword && formData.confirmPassword !== ''
                          ? "Passwords do not match"
                          : ""
                      }
                    />
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Details */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('profile.accountDetails')}
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('profile.role')}
                  </Typography>
                  <Chip 
                    label={user.role === 'super_admin' ? t('profile.superAdministrator') : t('profile.tenantAdministrator')}
                    color={user.role === 'super_admin' ? 'primary' : 'default'}
                    size="small"
                  />
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('profile.status')}
                  </Typography>
                  <Chip 
                    label={user.is_active ? t('profile.active') : t('profile.inactive')}
                    color={user.is_active ? 'success' : 'error'}
                    size="small"
                  />
                </Box>

                {user.tenant_count !== undefined && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('profile.tenants')}
                    </Typography>
                    <Typography variant="body2">
                      {user.tenant_count}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('profile.memberSince')}
                  </Typography>
                  <Typography variant="body2">
                    {format(new Date(user.created_at), 'MMMM dd, yyyy')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('profile.lastUpdated')}
                  </Typography>
                  <Typography variant="body2">
                    {format(new Date(user.updated_at), 'MMMM dd, yyyy')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Role Information */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('profile.permissions')}
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  • {t('profile.permissionViewManageTenants')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • {t('profile.permissionMonitorDeployments')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • {t('profile.permissionAccessLogs')}
                </Typography>
                {user.role === 'super_admin' && (
                  <>
                    <Typography variant="body2" color="primary">
                      • {t('profile.permissionManageUsers')}
                    </Typography>
                    <Typography variant="body2" color="primary">
                      • {t('profile.permissionViewAllTenants')}
                    </Typography>
                    <Typography variant="body2" color="primary">
                      • {t('profile.permissionSystemAdmin')}
                    </Typography>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;